import React, { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { LANGUAGES } from "@/i18n/translations";
import { LOGO_LIGHT } from "@/lib/images";
import { scrollToId } from "@/components/SmoothScroll";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { id: "vision", key: "vision" },
  { id: "location", key: "location" },
  { id: "investors", key: "investors" },
];

// Multilingual toggle temporarily hidden (English-only public site).
// Set to true to restore the EN / NL / NO switcher.
const SHOW_LANG_SWITCH = false;

export const Navigation = () => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onJump = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-[rgba(15,38,29,0.85)] backdrop-blur-md py-3"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => onJump("top")}
          className="flex items-center gap-3 group"
          aria-label="Yuzenya — home"
        >
          <img
            src={LOGO_LIGHT}
            alt="Yuzenya"
            className="h-11 w-11 object-contain"
          />
          <span className="font-serif text-ivory text-xl md:text-2xl tracking-[0.18em]">
            YUZENYA
          </span>
        </button>

        <nav className="hidden xl:flex items-center gap-6 mx-8 flex-1 justify-center">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => onJump(l.id)}
              className="overline text-ivory hover:text-bronze transition-colors duration-300 whitespace-nowrap"
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {SHOW_LANG_SWITCH && (
            <div className="hidden md:flex items-center gap-1 text-ivory/70" data-testid="lang-switch">
              {LANGUAGES.map((l, idx) => (
                <React.Fragment key={l.code}>
                  <button
                    data-testid={`lang-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`overline px-2 py-1 transition-colors ${
                      lang === l.code ? "text-bronze" : "hover:text-ivory"
                    }`}
                  >
                    {l.label}
                  </button>
                  {idx < LANGUAGES.length - 1 && <span className="text-ivory/30">/</span>}
                </React.Fragment>
              ))}
            </div>
          )}

          <button
            data-testid="nav-cta"
            onClick={() => onJump("waitlist")}
            className="hidden md:inline-flex btn-bronze overline px-5 py-3"
          >
            {t.hero.cta_primary}
          </button>

          <button
            data-testid="nav-mobile-toggle"
            className="xl:hidden text-ivory"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        data-testid="mobile-menu"
        className={`xl:hidden bg-forest border-t border-ivory/10 overflow-hidden transition-all duration-500 ${
          open ? "max-h-[600px]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-8 flex flex-col gap-6">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-${l.id}`}
              onClick={() => onJump(l.id)}
              className="font-serif text-ivory text-2xl text-left"
            >
              {t.nav[l.key]}
            </button>
          ))}
          {SHOW_LANG_SWITCH && (
            <div
              className="flex items-center gap-2 pt-6 mt-2 border-t border-ivory/10"
              data-testid="mobile-lang-switch"
            >
              {LANGUAGES.map((l, idx) => (
                <React.Fragment key={l.code}>
                  <button
                    data-testid={`mobile-lang-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`overline px-3 py-2 transition-colors ${
                      lang === l.code ? "text-bronze" : "text-ivory"
                    }`}
                    style={{ opacity: lang === l.code ? 1 : 0.85 }}
                  >
                    {l.label}
                  </button>
                  {idx < LANGUAGES.length - 1 && (
                    <span className="text-ivory/50" aria-hidden="true">
                      /
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
